/**
 * Number, currency, percent, and time formatting helpers.
 */

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const USD0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export const formatCurrency = (n: number): string => USD.format(n);
export const formatCurrency0 = (n: number): string => USD0.format(n);

export const formatNumber = (n: number, decimals = 0): string =>
  n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

export const formatPercent = (n: number, decimals = 2): string =>
  `${(n * 100).toFixed(decimals)}%`;

export const formatSignedPercent = (n: number, decimals = 2): string => {
  const sign = n > 0 ? '+' : '';
  return `${sign}${(n * 100).toFixed(decimals)}%`;
};

export const formatClock = (d: Date): string =>
  d.toLocaleTimeString('en-US', { hour12: false });

export const formatDate = (d: Date): string =>
  d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });

export const formatRelativeAge = (sinceMs: number): string => {
  const sec = Math.floor(sinceMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
};

/**
 * Compact dollar-amount formatter for status-bar chips and panel rows
 * where horizontal space is tighter than a heatmap cell. Uses M/B/K
 * suffixes and trims unnecessary trailing zeros at K-scale.
 * Moved here from lib/colors.ts — this is formatting, not color logic.
 */
export function formatCompact(value: number): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

/**
 * Format an option strike (or any strike-derived value such as a gamma
 * flip point) for display.
 *
 * Strikes are NOT always whole dollars: NVDA, AMD and most sub-$500
 * single names trade $2.50-wide ladders near the money (…217.5, 220,
 * 222.5…) and $0.50/$1 wide further out. The backend preserves the
 * exact strike end-to-end (`str(contract.strike)` → wire key "217.5"),
 * but every display site used `.toFixed(0)`, which rounds 217.5 → "218"
 * and silently mislabels every half-dollar strike.
 *
 * This prints the exact strike: whole strikes stay clean ("218", no
 * ".0"), fractional strikes keep up to 2 decimals with trailing zeros
 * trimmed ("217.5", "222.5", "7.25"). Half/quarter strikes are exactly
 * representable in IEEE-754, so the round-trip is lossless; the
 * Math.round guard only defends against any upstream float noise.
 */
export function formatStrike(strike: number): string {
  if (Number.isInteger(strike)) return String(strike);
  return String(Math.round(strike * 100) / 100);
}
